import { useState, useEffect } from 'react';
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import ConversionCell from "../../../components/UI/ConversionCell";
import {
    Save, Plus, ArrowUp, ArrowDown, Pencil, Copy, Trash2,
    DollarSign, Zap, Loader2,
} from 'lucide-react';
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { authService } from "../../../services/api";
import UpsellDefaultDeliveryCard from "./UpsellDefaultDeliveryCard";


export default function UpsellList({ upsells, loading, onEdit, onDuplicate, onNew, botId, currencySymbol = 'R$', revenueSymbol = 'R$', onDelete, defaultDelivery, onDefaultDeliveryChange }) {
    const { showError, showSuccess } = useNotification();

    // Faturamento é SEMPRE em BRL (mesmo padrão de dashboard/sales) → revenueSymbol ('R$').
    const fmtMoney = (v) => {
        const n = Number(v || 0);
        const sep = revenueSymbol.length > 1 ? ' ' : '';
        return `${revenueSymbol}${sep}${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const [list, setList] = useState([]);
    const [savingOrder, setSavingOrder] = useState(false);
    const [orderChanged, setOrderChanged] = useState(false);

    useEffect(() => {
        setList(upsells);
        setOrderChanged(false);
    }, [upsells]);


    const getTriggerBadge = (trigger) => {
        if (trigger === 'all') return <span className="text-green-500">Qualquer Pagamento</span>;
        if (trigger === 'downsell') return <span className="text-blue-500">Após Downsell</span>;
        if (trigger === 'disparo') return <span className="text-orange-500">Após Disparo</span>;
        return <span className="text-gray-500">{trigger}</span>;
    };

    // ---------- Reorder por botões (subir/descer prioridade) — igual /gateways ----------
    function move(index, dir) {
        const target = index + dir;
        if (target < 0 || target >= list.length) return;
        const updated = [...list];
        [updated[index], updated[target]] = [updated[target], updated[index]];
        setList(updated);
        setOrderChanged(true);
    }

    async function saveOrder() {
        setSavingOrder(true);
        try {
            const orderIds = list.map(u => u.id);
            const { data } = await authService.upsell.reorder(botId, orderIds);

            if (data.success) {
                showSuccess('Ordem dos upsells atualizada!');
                setOrderChanged(false);
            } else {
                showError(data.error || 'Erro ao atualizar ordem');
            }
        } catch (error) {
            showError('Erro ao salvar ordem dos upsells');
            console.error(error);
        } finally {
            setSavingOrder(false);
        }
    }

    const handleToggleActive = async (upsell) => {
        const next = !upsell.is_active;
        // Atualização otimista
        setList(prev => prev.map(u => u.id === upsell.id ? { ...u, is_active: next } : u));
        try {
            const { data } = await authService.upsell.toggle_active(botId, upsell.id, next);
            if (data.success) {
                showSuccess(next ? 'Upsell ativado' : 'Upsell desativado');
            } else {
                setList(prev => prev.map(u => u.id === upsell.id ? { ...u, is_active: upsell.is_active } : u));
                showError(data.error || 'Erro ao alternar upsell');
            }
        } catch (error) {
            setList(prev => prev.map(u => u.id === upsell.id ? { ...u, is_active: upsell.is_active } : u));
            showError('Erro ao alternar upsell');
        }
    };

    const renderActiveToggle = (upsell) => (
        <label
            className="toggle-wrapper"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            title={upsell.is_active ? 'Upsell ativo — clique para desativar' : 'Upsell desativado — clique para ativar'}
        >
            <input
                type="checkbox"
                checked={!!upsell.is_active}
                onChange={() => handleToggleActive(upsell)}
            />
            <span className="toggle-slider"></span>
        </label>
    );

    const handleDuplicate = async (upsellId) => {
        try {
            const { data } = await authService.upsell.duplicate_upsell(botId, upsellId);
            if (data.success) {
                showSuccess("Upsell duplicado!");
                onDuplicate?.();
            } else {
                showError(data.error || "Erro ao duplicar upsell");
            }
        } catch (error) {
            showError(error?.response?.data?.error || error.message || "Erro ao duplicar upsell");
        }
    };

    const handleDelete = async (upsellId) => {
        if (window.confirm("Tem certeza que deseja deletar este upsell?")) {
            try {
                const { data } = await authService.upsell.delete_upsell(botId, upsellId);
                if (data.success) {
                    showSuccess("Upsell deletado com sucesso!");
                    onDelete?.();
                } else {
                    showError(data.error || "Erro ao deletar upsell");
                }
            } catch (error) {
                showError(error.message || "Erro ao deletar upsell");
            }
        }
    };

    return (
        <>
            {loading ? (
                <TabSkeleton tab="upsell" />
            ) : (
                <div className="mt-6 flex flex-col lg:flex-row gap-6 items-start spt-reveal">
                  <div className="flex-1 min-w-0 w-full">

                    {list.length === 0 && (
                        <div className="empty-state">
                            <ArrowUp size={28} className="inline" />
                            <h4>Nenhum upsell criado</h4>
                            <p>Crie um novo upsell para aumentar sua conversão.</p>
                        </div>
                    )}

                    {/* DESKTOP */}
                    {list.length > 0 && (
                        <div className="sx-desktop">
                            <table className="sx-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '70px', textAlign: 'center' }}></th>

                                        {/* <th className='w-[100px]'>Status</th> */}
                                        <th className='w-[180px]'>Nome</th>
                                        {/* <th>Posição</th> */}
                                        <th>Delay</th>
                                        <th>Faturamento</th>
                                        <th>Conversão</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((upsell, index) => (
                                        <tr
                                            key={upsell.id}
                                            style={{ opacity: upsell.is_active ? 1 : 0.5 }}
                                        >
                                            <td style={{ textAlign: 'center', padding: '0 8px' }}>
                                                <div className="flex gap-1">
                                                    <button type="button" className="reorder-btn" aria-label="Subir prioridade" disabled={index === 0}
                                                        onClick={() => move(index, -1)}><ArrowUp size={15} /></button>
                                                    <button type="button" className="reorder-btn" aria-label="Descer prioridade" disabled={index === list.length - 1}
                                                        onClick={() => move(index, 1)}><ArrowDown size={15} /></button>
                                                </div>
                                            </td>
                                            {/* <td style={{ textAlign: 'center' }}>
                                                {renderActiveToggle(upsell)}
                                            </td> */}
                                            <td className='max-w-[180px] truncate' title={upsell.name}>{upsell.name}</td>
                                            {/* <td className='w-[150px] max-w-[150px] truncate'>{getTriggerBadge(upsell.trigger_after)}</td> */}
                                            {/* <td>#{upsell.position_in_chain}</td> */}
                                            <td>{upsell.delay_minutes === 0 ? 'Imediato' : `${upsell.delay_minutes}min`}</td>
                                            <td className="font-semibold text-green-600 whitespace-nowrap">{fmtMoney(upsell.revenue)}</td>
                                            <td className="whitespace-nowrap"><ConversionCell paid={upsell.paid_count ?? 0} generated={upsell.generated_count ?? 0} /></td>

                                            <td>
                                                <div className="flex items-center gap-2">

                                                    <button
                                                        type="button"
                                                        onClick={() => handleDuplicate(upsell.id)}
                                                        className="btn-secondary btn-action"
                                                        title="Duplicar upsell"
                                                    >
                                                        <Copy size={16} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => onEdit(upsell)}
                                                        className="btn-primary btn-action"
                                                        title="Editar upsell"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(upsell.id)}
                                                        className="btn-delete-2 btn-action"
                                                        title="Excluir upsell"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* MOBILE — inspirado nos Redirecionadores: lista contínua (sem card avulso),
                        chip roxo, pills e métricas em grid, no padrão de cores da Spectrum. */}
                    <div className="sx-mobile">
                        <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
                            {list.map((upsell, index) => (
                                <div
                                    key={upsell.id}
                                    className={`p-4 ${index > 0 ? 'border-t border-white/[0.06]' : ''} ${upsell.is_active ? '' : 'opacity-60'}`}
                                >
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa] font-bold text-sm flex-shrink-0" title="Posição na cadeia">
                                                #{upsell.position_in_chain}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate" title={upsell.name}>{upsell.name}</div>
                                                <div className="flex items-center gap-2 flex-wrap mt-1">
                                                    <span className="sx-pill sx-pill--purple">{upsell.delay_minutes === 0 ? 'Imediato' : `${upsell.delay_minutes} min`}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button type="button" className="reorder-btn" aria-label="Subir prioridade" disabled={index === 0}
                                                onClick={() => move(index, -1)}><ArrowUp size={15} /></button>
                                            <button type="button" className="reorder-btn" aria-label="Descer prioridade" disabled={index === list.length - 1}
                                                onClick={() => move(index, 1)}><ArrowDown size={15} /></button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] px-3 py-2 min-w-0">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/35">
                                                <DollarSign size={11} className="text-[#4ade80]" /> Faturamento
                                            </div>
                                            <div className="text-sm font-bold text-[#4ade80] truncate">{fmtMoney(upsell.revenue)}</div>
                                        </div>
                                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] px-3 py-2 min-w-0">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/35">
                                                <Zap size={11} className="text-[#60a5fa]" /> Conversão
                                            </div>
                                            <div className="text-sm font-bold text-white truncate"><ConversionCell paid={upsell.paid_count ?? 0} generated={upsell.generated_count ?? 0} /></div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => onEdit(upsell)} className="btn-primary btn-action flex-1 justify-center">
                                            <Pencil size={14} /> Editar
                                        </button>
                                        <button type="button" onClick={() => handleDuplicate(upsell.id)} className="btn-secondary btn-action flex-1 justify-center">
                                            <Copy size={14} /> Duplicar
                                        </button>
                                        <button type="button" onClick={() => handleDelete(upsell.id)} className="btn-delete-2 btn-action" title="Excluir upsell">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {orderChanged && (
                        <button
                            type="button"
                            onClick={saveOrder}
                            disabled={savingOrder}
                            className="btn-primary w-full block mt-4"
                            title="Salvar a nova ordem dos upsells"
                        >
                            {savingOrder ? (
                                <><Loader2 size={14} className="animate-spin me-2 inline" /> Salvando...</>
                            ) : (
                                <><Save size={14} className="me-2 inline" /> Salvar Ordem</>
                            )}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onNew}
                        className="w-full flex items-center justify-center gap-2 py-2.5 mt-4 rounded-xl border border-dashed border-white/[0.08] text-white/40 hover:text-[var(--purple)] hover:border-[var(--purple-dark)] hover:bg-[var(--bg-primary)] transition-colors text-sm"
                    >
                        <Plus size={16} /> Novo Upsell
                    </button>

                  </div>

                  <aside className="w-full lg:w-[340px] shrink-0 space-y-4">
                    <UpsellDefaultDeliveryCard
                        botId={botId}
                        value={defaultDelivery}
                        onChange={onDefaultDeliveryChange}
                    />
                  </aside>

                </div>
            )}
        </>
    );
}
